'use client';

import { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, Loader2, DownloadCloud, ChevronUp, ChevronDown } from 'lucide-react';
import { useSession } from 'next-auth/react';

interface RepoConfig {
  id: string;
  platform: 'github' | 'gitlab';
  repo: string;
  branch: string;
  since: string;
  until: string;
  keterangan: string;
}

// Reusable SearchableSelect
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Cari...',
  loading = false,
  disabled = false
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()) || o.value.toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setHighlightIdx(0); }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlightIdx]) {
      e.preventDefault();
      onChange(filtered[highlightIdx].value);
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        className={`input-field ${disabled ? 'disabled' : ''}`}
        onClick={() => { if (!disabled) { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); } }}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', minHeight: 44, gap: 8, opacity: disabled ? 0.6 : 1
        }}
      >
        {loading ? (
          <span style={{ flex: 1, fontSize: 13, color: 'var(--text-muted)' }}><Loader2 size={14} className="animate-spin" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }}/> Memuat...</span>
        ) : isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              border: 'none', outline: 'none', background: 'transparent',
              flex: 1, fontSize: 13, color: 'var(--text-primary)',
              fontFamily: 'inherit',
            }}
          />
        ) : (
          <span style={{
            flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          }}>
            {selectedLabel || placeholder}
          </span>
        )}
        <span style={{ fontSize: 10, color: 'var(--text-muted)', flexShrink: 0 }}>
          {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          marginTop: 4, background: 'var(--bg-primary)',
          border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          maxHeight: 220, overflowY: 'auto', zIndex: 50,
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
              Tidak ditemukan
            </div>
          ) : (
            filtered.map((opt, idx) => (
              <div
                key={opt.value}
                onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                style={{
                  padding: '10px 16px', fontSize: 13, cursor: 'pointer',
                  lineHeight: 1.5,
                  background: idx === highlightIdx
                    ? 'rgba(99, 102, 241, 0.1)'
                    : opt.value === value
                      ? 'rgba(99, 102, 241, 0.05)'
                      : 'transparent',
                  color: opt.value === value ? 'var(--accent)' : 'var(--text-primary)',
                  fontWeight: opt.value === value ? 600 : 400,
                  borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function GitImportModal({
  isOpen,
  onClose,
  availablePlatforms,
  onSuccess
}: {
  isOpen: boolean;
  onClose: () => void;
  availablePlatforms: string[];
  onSuccess: (commitsText: string) => void;
}) {
  const { data: session } = useSession();
  
  // Data Caches
  const [reposCache, setReposCache] = useState<{ [platform: string]: any[] }>({});
  const [branchesCache, setBranchesCache] = useState<{ [repoKey: string]: any[] }>({});
  const [loadingRepos, setLoadingRepos] = useState<{ [platform: string]: boolean }>({});
  const [loadingBranches, setLoadingBranches] = useState<{ [repoKey: string]: boolean }>({});

  const defaultPlatform = availablePlatforms && availablePlatforms.length > 0 ? (availablePlatforms[0] as 'github' | 'gitlab') : 'github';
  
  const [configs, setConfigs] = useState<RepoConfig[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nip = (session as any)?.nip;

  useEffect(() => {
    if (isOpen) {
        setConfigs([{
            id: Date.now().toString(),
            platform: defaultPlatform,
            repo: '',
            branch: '',
            since: '',
            until: '',
            keterangan: ''
        }]);
        setError('');
    }
  }, [isOpen, defaultPlatform]);

  useEffect(() => {
    if (!nip || !isOpen) return;
    
    configs.forEach(config => {
        const plat = config.platform;
        if (!reposCache[plat] && !loadingRepos[plat]) {
            setLoadingRepos(prev => ({ ...prev, [plat]: true }));
            fetch(`/api/git/${plat}/repos?nip=${nip}`)
                .then(r => r.json())
                .then(res => {
                    if (res.data) {
                        setReposCache(prev => ({ ...prev, [plat]: res.data }));
                    }
                })
                .catch(err => console.error(err))
                .finally(() => {
                    setLoadingRepos(prev => ({ ...prev, [plat]: false }));
                });
        }
        
        // Fetch branches for repo if not cached
        if (config.repo) {
            const repoKey = `${plat}:${config.repo}`;
            if (!branchesCache[repoKey] && !loadingBranches[repoKey]) {
                setLoadingBranches(prev => ({ ...prev, [repoKey]: true }));
                fetch(`/api/git/${plat}/branches?nip=${nip}&repo=${encodeURIComponent(config.repo)}`)
                    .then(r => r.json())
                    .then(res => {
                        if (res.data) {
                            setBranchesCache(prev => ({ ...prev, [repoKey]: res.data }));
                            if (!config.branch && res.data.length > 0) {
                                const main = res.data.find((b: any) => b.name === 'main' || b.name === 'master');
                                updateConfig(config.id, 'branch', main ? main.name : res.data[0].name);
                            }
                        }
                    })
                    .catch(err => console.error(err))
                    .finally(() => {
                        setLoadingBranches(prev => ({ ...prev, [repoKey]: false }));
                    });
            }
        }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, nip, isOpen]); // removed reposCache, branchesCache etc to avoid loop

  if (!isOpen) return null;

  const addConfig = () => {
    setConfigs([...configs, {
      id: Date.now().toString(),
      platform: defaultPlatform,
      repo: '',
      branch: '',
      since: '',
      until: '',
      keterangan: ''
    }]);
  };

  const removeConfig = (id: string) => {
    if (configs.length > 1) {
      setConfigs(configs.filter(c => c.id !== id));
    }
  };

  const updateConfig = (id: string, field: keyof RepoConfig, value: string) => {
    setConfigs(prev => prev.map(c => {
        if (c.id === id) {
            const updated = { ...c, [field]: value };
            if (field === 'platform') {
                updated.repo = '';
                updated.branch = '';
            }
            if (field === 'repo') {
                updated.branch = '';
            }
            return updated;
        }
        return c;
    }));
  };

  const handleFetch = async () => {
    if (!nip) {
        setError("NIP tidak ditemukan. Harap login ulang.");
        return;
    }

    const invalid = configs.find(c => !c.repo || !c.branch || !c.since || !c.until);
    if (invalid) {
      setError('Mohon lengkapi platform, repository, branch, tanggal mulai, dan tanggal selesai untuk semua baris.');
      return;
    }

    setIsFetching(true);
    setError('');
    let combinedOutput = '';

    try {
      for (const config of configs) {
        const endpoint = config.platform === 'github' ? '/api/git/github' : '/api/git/gitlab';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nip,
            repo: config.repo,
            branch: config.branch,
            since: config.since,
            until: config.until,
            keterangan: config.keterangan
          })
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || `Gagal fetch dari ${config.platform}`);
        }

        if (data.data) {
          combinedOutput += data.data + '\n';
        }
      }

      onSuccess(combinedOutput.trim());
      onClose();
    } catch (err: any) {
      setError(err.message || 'Terjadi kesalahan saat mengambil commit.');
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', justifyContent: 'center', alignItems: 'center'
    }}>
      <div className="animate-slide-up" style={{
        backgroundColor: 'var(--bg-primary)', borderRadius: 16, width: '90%', maxWidth: 900,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh', border: '1px solid var(--border)'
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <DownloadCloud size={20} /> Import dari Git
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {error && <div style={{ padding: 12, marginBottom: 16, backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13, border: '1px solid #fca5a5' }}>{error}</div>}

          {configs.map((config, index) => {
            const platformRepos = reposCache[config.platform] || [];
            const isRepoLoading = loadingRepos[config.platform] || false;
            
            const repoKey = `${config.platform}:${config.repo}`;
            const repoBranches = branchesCache[repoKey] || [];
            const isBranchLoading = config.repo ? (loadingBranches[repoKey] || false) : false;

            const repoOptions = platformRepos.map(r => ({
                value: r.full_name || r.path_with_namespace,
                label: r.full_name || r.path_with_namespace
            }));
            
            const branchOptions = repoBranches.map(b => ({
                value: b.name,
                label: b.name
            }));

            return (
                <div key={config.id} style={{ marginBottom: 24, padding: 20, border: '1px solid var(--border)', borderRadius: 12, backgroundColor: 'var(--bg-secondary)', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Repository #{index + 1}</span>
                    {configs.length > 1 && (
                      <button onClick={() => removeConfig(config.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label className="label">Platform</label>
                      <select 
                        className="input-field" 
                        value={config.platform}
                        onChange={e => updateConfig(config.id, 'platform', e.target.value)}
                        style={{ width: '100%', display: 'flex', alignItems: 'center' }}
                      >
                        {availablePlatforms?.includes('github') && <option value="github">GitHub</option>}
                        {availablePlatforms?.includes('gitlab') && <option value="gitlab">GitLab</option>}
                      </select>
                    </div>
                    <div>
                      <label className="label">Repository</label>
                      <SearchableSelect 
                        options={repoOptions}
                        value={config.repo}
                        onChange={val => updateConfig(config.id, 'repo', val)}
                        placeholder="Pilih Repository..."
                        loading={isRepoLoading}
                      />
                    </div>
                    <div>
                      <label className="label">Branch</label>
                      <SearchableSelect 
                        options={branchOptions}
                        value={config.branch}
                        onChange={val => updateConfig(config.id, 'branch', val)}
                        placeholder="Pilih Branch..."
                        loading={isBranchLoading}
                        disabled={!config.repo}
                      />
                    </div>
                  </div>

                  {reposCache[config.platform] && platformRepos.length === 0 && !isRepoLoading && (
                    <div style={{ padding: 12, marginBottom: 16, backgroundColor: 'rgba(234, 179, 8, 0.1)', color: '#ca8a04', borderRadius: 8, fontSize: 13, border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                      Daftar repository {config.platform === 'github' ? 'GitHub' : 'GitLab'} kosong. Harap sinkronisasi (fetch) repository terlebih dahulu di <a href="/settings" style={{ color: '#ca8a04', textDecoration: 'underline', fontWeight: 600 }}>menu Pengaturan</a>.
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 16 }}>
                    <div>
                      <label className="label">Sejak Tanggal</label>
                      <input 
                        type="date" 
                        className="input-field" 
                        value={config.since}
                        onChange={e => updateConfig(config.id, 'since', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="label">Hingga Tanggal</label>
                      <input 
                        type="date" 
                        className="input-field" 
                        value={config.until}
                        onChange={e => updateConfig(config.id, 'until', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="label">Keterangan / Nama Aplikasi</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        placeholder="misal: LaporMasBup"
                        value={config.keterangan}
                        onChange={e => updateConfig(config.id, 'keterangan', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
            );
          })}

          <button onClick={addConfig} className="btn-secondary" style={{ width: '100%', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, borderStyle: 'dashed' }}>
            <Plus size={16} /> Tambah Repository Lain
          </button>
        </div>

        {/* Footer */}
        <div style={{ padding: '20px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Pastikan token Git telah disetel di <a href="/settings" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>Pengaturan</a>
          </span>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={onClose} disabled={isFetching} className="btn-secondary" style={{ padding: '10px 24px' }}>
              Batal
            </button>
            <button
              onClick={handleFetch}
              disabled={isFetching}
              className="btn-primary"
              style={{
                padding: '10px 24px',
                display: 'flex', alignItems: 'center', gap: 8,
                opacity: isFetching ? 0.7 : 1,
                cursor: isFetching ? 'not-allowed' : 'pointer'
              }}
            >
              {isFetching ? <Loader2 size={16} className="animate-spin" /> : <DownloadCloud size={16} />}
              {isFetching ? 'Mengambil Data...' : 'Tarik Commits'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
